import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Calendar } from "lucide-react";
import BannedWordsList from "@/components/admin/BannedWordsList";
import QuestionableReviewsList from "@/components/admin/QuestionableReviewsList";
import type { Review } from "@/types/review";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { useIsMobile } from "@/hooks/use-mobile";
import { LoadingSpinner } from "@/components/ui/loading-spinner";

interface BannedWord {
  id: string;
  word: string;
  created_at: string;
}

export default function AdminReviews() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [isDeletingOld, setIsDeletingOld] = useState(false);
  const [bannedWords, setBannedWords] = useState<BannedWord[]>([]);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [reportedReviewIds, setReportedReviewIds] = useState<string[]>([]);
  const isMobile = useIsMobile();

  useEffect(() => {
    checkAdminAccess();
  }, []);

  const checkAdminAccess = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate('/');
        return;
      }

      const { data: profile } = await supabase
        .from('profiles')
        .select('is_admin')
        .eq('id', user.id)
        .single();

      if (!profile?.is_admin) {
        toast.error("You don't have permission to access this page");
        navigate('/');
        return;
      }

      setIsLoading(false);
      fetchBannedWords();
      fetchReviews();
      fetchReportedReviews();
    } catch (error: any) {
      toast.error(error.message);
      navigate('/');
    }
  };

  const handleDeleteOldReviews = async () => {
    if (!window.confirm('Are you sure you want to delete all reviews older than 2 years? This action cannot be undone.')) {
      return;
    }

    setIsDeletingOld(true);
    try {
      const { error } = await supabase.rpc('delete_old_reviews');
      if (error) throw error;

      toast.success('Successfully deleted old reviews');
      fetchReviews(); // Refresh the reviews list
    } catch (error: any) {
      toast.error('Failed to delete old reviews: ' + error.message);
    } finally {
      setIsDeletingOld(false);
    }
  };

  const fetchBannedWords = async () => {
    try {
      const { data, error } = await supabase
        .from('banned_words')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setBannedWords(data || []);
    } catch (error: any) {
      toast.error("Failed to fetch banned words");
      setBannedWords([]);
    }
  };

  const fetchReportedReviews = async () => {
    try {
      const { data, error } = await supabase
        .from('review_reports')
        .select('review_id');

      if (error) throw error;
      setReportedReviewIds(data.map(report => report.review_id));
    } catch (error: any) {
      toast.error("Failed to fetch reported reviews");
      setReportedReviewIds([]);
    }
  };

  const fetchReviews = async () => {
    try {
      const { data, error } = await supabase
        .from('reviews')
        .select(`
          id, 
          airport_name,
          airport_location,
          review_text,
          film_friendly_score,
          hand_check_success, 
          created_at,
          user_id,
          profiles(username, avatar_url, link_one, link_two)
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setReviews(data || []);
    } catch (error: any) {
      toast.error("Failed to fetch reviews");
      setReviews([]);
    }
  };

  const checkReviewForBannedWords = (reviewText: string): string[] => {
    const foundWords: string[] = [];
    const wordsInReview = reviewText.toLowerCase().match(/\b\w+\b/g) || [];
    
    (bannedWords || []).forEach(({ word }) => {
      const bannedWordPattern = new RegExp(`\\b${word.toLowerCase()}\\b`);
      if (wordsInReview.some(reviewWord => bannedWordPattern.test(reviewWord))) {
        foundWords.push(word);
      }
    });
    return foundWords;
  };

  const questionableReviews = (reviews || []).filter(review => 
    checkReviewForBannedWords(review.review_text).length > 0 || 
    reportedReviewIds.includes(review.id)
  ).map(review => ({
    ...review,
    reported: reportedReviewIds.includes(review.id)
  }));

  const handleReviewsChange = () => {
    fetchReviews();
    fetchReportedReviews();
  };

  if (isLoading) {
    return (
      <div className="container mx-auto py-8 px-4 flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <AdminLayout title="Review Moderation">
      <div className="flex justify-end">
        <Button
          variant="outline"
          onClick={handleDeleteOldReviews}
          disabled={isDeletingOld}
          className="gap-2"
          size={isMobile ? "sm" : "default"}
        >
          <Calendar className={isMobile ? "h-3 w-3" : "h-4 w-4"} />
          {isDeletingOld ? 'Deleting...' : isMobile ? 'Delete Old' : 'Delete Reviews Older Than 2 Years'}
        </Button>
      </div>

      <div className="space-y-6">
        <BannedWordsList 
          bannedWords={bannedWords} 
          onWordsChange={fetchBannedWords} 
        />
        <QuestionableReviewsList 
          reviews={questionableReviews}
          checkReviewForBannedWords={checkReviewForBannedWords}
          onReviewsChange={handleReviewsChange}
        />
      </div>
    </AdminLayout>
  );
}
